import { Component, OnInit, inject, Inject, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { SuppliersService } from '../../core/services/suppliers.service';
import { Supplier, SupplierTotals, CreateSupplierDto, UpdateSupplierDto, BankAccountRequest, BankAccount } from '../../core/models/supplier.model';
import { MatTableModule } from '@angular/material/table';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatDialog, MatDialogModule, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { CommonModule, DecimalPipe } from '@angular/common';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { FormBuilder, FormArray, ReactiveFormsModule, Validators, FormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatCardModule } from '@angular/material/card';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

@Component({
  selector: 'app-suppliers',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatTableModule,
    MatButtonModule,
    MatIconModule,
    MatDialogModule,
    MatSnackBarModule,
    DecimalPipe,
    MatFormFieldModule,
    MatInputModule,
    MatSlideToggleModule,
    MatTooltipModule,
    MatCardModule,
    MatProgressSpinnerModule
  ],
  template: `
    <div class="page-header">
      <div>
        <h1 class="page-title">Dobavljači - Pregled</h1>
        <p class="page-subtitle">Lista svih dobavljača sa ukupnim iznosima dugovanja</p>
      </div>
      <button mat-raised-button color="primary" (click)="openCreate()" class="create-button">
        <mat-icon>add</mat-icon>
        Novi dobavljač
      </button>
    </div>

    <!-- Summary Cards -->
    <div class="summary-cards" *ngIf="data.length">
      <div class="summary-card">
        <div class="card-icon blue">
          <mat-icon>local_shipping</mat-icon>
        </div>
        <div class="card-content">
          <div class="card-value">{{ data.length }}</div>
          <div class="card-label">Ukupno dobavljača</div>
        </div>
      </div>
      <div class="summary-card">
        <div class="card-icon green">
          <mat-icon>account_balance_wallet</mat-icon>
        </div>
        <div class="card-content">
          <div class="card-value">{{ totalAmount | number:'1.2-2' }} RSD</div>
          <div class="card-label">Ukupno za plaćanje</div>
        </div>
      </div>
      <div class="summary-card">
        <div class="card-icon purple">
          <mat-icon>payments</mat-icon>
        </div>
        <div class="card-content">
          <div class="card-value">{{ totalPaid | number:'1.2-2' }} RSD</div>
          <div class="card-label">Ukupno plaćeno</div>
        </div>
      </div>
      <div class="summary-card">
        <div class="card-icon orange">
          <mat-icon>pending_actions</mat-icon>
        </div>
        <div class="card-content">
          <div class="card-value">{{ totalRemaining | number:'1.2-2' }} RSD</div>
          <div class="card-label">Preostalo</div>
        </div>
      </div>
    </div>

    <!-- Filters -->
    <div class="filters-row">
      <mat-form-field appearance="outline" class="search-field">
        <mat-label>Pretraži dobavljača</mat-label>
        <input matInput [(ngModel)]="searchText" (ngModelChange)="onSearch()" placeholder="Unesite ime...">
        <mat-icon matSuffix>search</mat-icon>
      </mat-form-field>
      <mat-slide-toggle [(ngModel)]="activeOnly" (change)="load()">
        Samo aktivni
      </mat-slide-toggle>
    </div>

    <!-- Loading -->
    <div class="loading-container" *ngIf="loading">
      <mat-spinner diameter="40"></mat-spinner>
    </div>

    <!-- Table -->
    <div class="table-container" *ngIf="!loading && data.length">
      <table mat-table [dataSource]="data" class="suppliers-table">
        <ng-container matColumnDef="name">
          <th mat-header-cell *matHeaderCellDef>Naziv firme</th>
          <td mat-cell *matCellDef="let s" class="clickable-cell" (click)="viewSupplier(s)">
            <div class="supplier-name">
              {{ s.name }}
              <span class="status-dot" [ngClass]="s.isActive ? 'active' : 'inactive'"
                    [matTooltip]="s.isActive ? 'Aktivan' : 'Neaktivan'"></span>
            </div>
          </td>
        </ng-container>
        <ng-container matColumnDef="totalAmount">
          <th mat-header-cell *matHeaderCellDef class="text-right">Ukupno za plaćanje</th>
          <td mat-cell *matCellDef="let s" class="text-right amount-cell">{{ s.totalAmount | number:'1.2-2' }}</td>
        </ng-container>
        <ng-container matColumnDef="totalPaid">
          <th mat-header-cell *matHeaderCellDef class="text-right">Plaćeno</th>
          <td mat-cell *matCellDef="let s" class="text-right amount-cell paid">{{ s.totalPaid | number:'1.2-2' }}</td>
        </ng-container>
        <ng-container matColumnDef="totalRemaining">
          <th mat-header-cell *matHeaderCellDef class="text-right">Preostalo</th>
          <td mat-cell *matCellDef="let s" class="text-right amount-cell" [ngClass]="{'remaining': s.totalRemaining > 0}">
            {{ s.totalRemaining | number:'1.2-2' }}
          </td>
        </ng-container>
        <ng-container matColumnDef="overdueCount">
          <th mat-header-cell *matHeaderCellDef class="text-center">Dospelo</th>
          <td mat-cell *matCellDef="let s" class="text-center">
            <span class="overdue-badge" *ngIf="s.overdueCount > 0">{{ s.overdueCount }}</span>
            <span class="no-overdue" *ngIf="s.overdueCount === 0">-</span>
          </td>
        </ng-container>
        <ng-container matColumnDef="actions">
          <th mat-header-cell *matHeaderCellDef class="table-actions">Akcije</th>
          <td mat-cell *matCellDef="let s" class="table-actions">
            <button mat-icon-button (click)="viewSupplier(s)" matTooltip="Pregledaj dugovanja" class="action-button">
              <mat-icon>visibility</mat-icon>
            </button>
            <button mat-icon-button (click)="openEdit(s)" matTooltip="Izmeni" class="action-button edit-button">
              <mat-icon>edit</mat-icon>
            </button>
            <button mat-icon-button (click)="confirmDelete(s)" matTooltip="Obriši" class="action-button delete-button">
              <mat-icon>delete</mat-icon>
            </button>
          </td>
        </ng-container>
        <tr mat-header-row *matHeaderRowDef="cols"></tr>
        <tr mat-row *matRowDef="let row; columns: cols;" class="table-row"></tr>
      </table>
    </div>

    <div class="empty-state" *ngIf="!loading && !data.length">
      <mat-icon>local_shipping</mat-icon>
      <p>Nema pronađenih dobavljača</p>
      <button mat-stroked-button color="primary" (click)="openCreate()">Dodaj prvog dobavljača</button>
    </div>
  `,
  styles: [`
    .page-header {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      margin-bottom: 24px;
    }

    .page-title {
      margin: 0;
      font-size: 28px;
      font-weight: 600;
      color: #1F2937;
    }

    .page-subtitle {
      margin: 4px 0 0 0;
      color: #6B7280;
      font-size: 14px;
    }

    .create-button {
      height: 42px;
      border-radius: 4px;
      font-weight: 600;
      background-color: #3B82F6 !important;
      color: #FFFFFF !important;
      box-shadow: 0 2px 8px rgba(59, 130, 246, 0.25);
    }

    .create-button:hover {
      box-shadow: 0 4px 12px rgba(59, 130, 246, 0.35);
    }

    .create-button mat-icon {
      margin-right: 8px;
    }

    .summary-cards {
      display: grid;
      grid-template-columns: repeat(4, 1fr);
      gap: 16px;
      margin-bottom: 24px;
    }

    @media (max-width: 1200px) {
      .summary-cards {
        grid-template-columns: repeat(2, 1fr);
      }
    }

    @media (max-width: 600px) {
      .summary-cards {
        grid-template-columns: 1fr;
      }
    }

    .summary-card {
      background: white;
      border-radius: 8px;
      padding: 20px;
      display: flex;
      align-items: center;
      gap: 16px;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
      border: 1px solid #E5E7EB;
    }

    .card-icon {
      width: 48px;
      height: 48px;
      border-radius: 12px;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .card-icon mat-icon {
      color: white;
      font-size: 24px;
      width: 24px;
      height: 24px;
    }

    .card-icon.blue { background: linear-gradient(135deg, #3B82F6, #2563EB); }
    .card-icon.green { background: linear-gradient(135deg, #10B981, #059669); }
    .card-icon.purple { background: linear-gradient(135deg, #8B5CF6, #7C3AED); }
    .card-icon.orange { background: linear-gradient(135deg, #F59E0B, #D97706); }

    .card-content {
      flex: 1;
    }

    .card-value {
      font-size: 20px;
      font-weight: 700;
      color: #1F2937;
    }

    .card-label {
      font-size: 13px;
      color: #6B7280;
      margin-top: 2px;
    }

    .filters-row {
      display: flex;
      align-items: center;
      gap: 24px;
      margin-bottom: 16px;
    }

    .search-field {
      width: 300px;
    }

    .loading-container {
      display: flex;
      justify-content: center;
      padding: 60px;
    }

    .table-container {
      background: white;
      border-radius: 8px;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
      overflow: hidden;
      border: 1px solid #E5E7EB;
    }

    .suppliers-table {
      width: 100%;
      background: white;
    }

    .suppliers-table th {
      background: #FFFFFF;
      color: #4B5563;
      font-weight: 600;
      padding: 12px 16px !important;
      border-bottom: 1px solid #D1D5DB;
    }

    .suppliers-table td {
      padding: 12px 16px !important;
      color: #1F2937;
      font-size: 14px;
      border-bottom: 1px solid #E5E7EB;
    }

    .clickable-cell {
      cursor: pointer;
    }

    .clickable-cell:hover {
      color: #3B82F6;
    }

    .supplier-name {
      display: flex;
      align-items: center;
      gap: 8px;
      font-weight: 500;
    }

    .status-dot {
      width: 8px;
      height: 8px;
      border-radius: 50%;
    }

    .status-dot.active { background: #10B981; }
    .status-dot.inactive { background: #EF4444; }

    .text-right {
      text-align: right !important;
    }

    .text-center {
      text-align: center !important;
    }

    .amount-cell {
      font-family: 'Roboto Mono', monospace;
      font-weight: 500;
    }

    .amount-cell.paid {
      color: #10B981;
    }

    .amount-cell.remaining {
      color: #F59E0B;
      font-weight: 600;
    }

    .overdue-badge {
      background: #FEE2E2;
      color: #DC2626;
      padding: 4px 10px;
      border-radius: 12px;
      font-size: 12px;
      font-weight: 600;
    }

    .no-overdue {
      color: #9CA3AF;
    }

    .table-actions {
      text-align: right !important;
      width: 140px;
    }

    .action-button {
      transition: all 0.2s ease;
    }

    .action-button:hover {
      background-color: rgba(0, 0, 0, 0.06);
    }

    .empty-state {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      padding: 80px 20px;
      background: white;
      border-radius: 10px;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
      border: 1px solid #E5E7EB;
    }

    .empty-state mat-icon {
      font-size: 64px;
      width: 64px;
      height: 64px;
      color: #D1D5DB;
      margin-bottom: 16px;
    }

    .empty-state p {
      color: #6B7280;
      font-size: 16px;
      margin: 0 0 16px 0;
    }
  `]
})
export class SuppliersComponent implements OnInit {
  private service = inject(SuppliersService);
  private dialog = inject(MatDialog);
  private snack = inject(MatSnackBar);
  private router = inject(Router);

  cols = ['name', 'totalAmount', 'totalPaid', 'totalRemaining', 'overdueCount', 'actions'];
  data: SupplierTotals[] = [];
  loading = false;
  activeOnly = false;
  searchText = '';

  // Computed totals
  totalAmount = 0;
  totalPaid = 0;
  totalRemaining = 0;

  ngOnInit(): void {
    this.load();
  }

  load() {
    this.loading = true;
    this.service.getAllWithTotals(this.activeOnly || undefined, this.searchText || undefined).subscribe({
      next: (list) => {
        this.data = list;
        this.calculateTotals();
        this.loading = false;
      },
      error: () => {
        this.snack.open('Greška pri učitavanju podataka', 'Zatvori', { duration: 3000 });
        this.loading = false;
      }
    });
  }

  calculateTotals() {
    this.totalAmount = this.data.reduce((sum, s) => sum + s.totalAmount, 0);
    this.totalPaid = this.data.reduce((sum, s) => sum + s.totalPaid, 0);
    this.totalRemaining = this.data.reduce((sum, s) => sum + s.totalRemaining, 0);
  }

  onSearch() {
    this.load();
  }

  viewSupplier(supplier: SupplierTotals) {
    this.router.navigate(['/suppliers', supplier.id]);
  }

  openCreate() {
    const ref = this.dialog.open(SupplierDialogComponent, { data: null });
    ref.afterClosed().subscribe((dto?: CreateSupplierDto) => {
      if (!dto) return;
      this.service.create(dto).subscribe({
        next: () => {
          this.snack.open('Dobavljač je kreiran', 'Zatvori', { duration: 2000 });
          this.load();
        },
        error: (e) => this.snack.open(e?.error?.message || 'Greška pri čuvanju', 'Zatvori', { duration: 3000 })
      });
    });
  }

  openEdit(supplier: SupplierTotals) {
    const ref = this.dialog.open(SupplierDialogComponent, { data: supplier });
    ref.afterClosed().subscribe((dto?: UpdateSupplierDto) => {
      if (!dto) return;
      this.service.update(supplier.id, dto).subscribe({
        next: () => {
          this.snack.open('Dobavljač je ažuriran', 'Zatvori', { duration: 2000 });
          this.load();
        },
        error: (e) => this.snack.open(e?.error?.message || 'Greška pri čuvanju', 'Zatvori', { duration: 3000 })
      });
    });
  }

  confirmDelete(supplier: SupplierTotals) {
    const yes = confirm(`Da li ste sigurni da želite da obrišete dobavljača "${supplier.name}"? Sva povezana dugovanja će takođe biti obrisana.`);
    if (!yes) return;
    this.service.delete(supplier.id).subscribe({
      next: () => {
        this.snack.open('Dobavljač je obrisan', 'Zatvori', { duration: 2000 });
        this.load();
      },
      error: (e) => this.snack.open(e?.error?.message || 'Greška pri brisanju', 'Zatvori', { duration: 3000 })
    });
  }
}

@Component({
  selector: 'app-supplier-dialog',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatCheckboxModule,
    MatButtonModule,
    MatDialogModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatTooltipModule
  ],
  template: `
    <h2 mat-dialog-title>{{ data ? 'Izmeni dobavljača' : 'Novi dobavljač' }}</h2>

    <div class="loading-dialog" *ngIf="loadingBankAccounts">
      <mat-spinner diameter="30"></mat-spinner>
      <span>Učitavanje podataka...</span>
    </div>

    <form [formGroup]="form" (ngSubmit)="save()" class="supplier-form" *ngIf="!loadingBankAccounts">
      <div class="form-section">
        <h3 class="section-title">Osnovni podaci</h3>
        <mat-form-field appearance="outline" class="full-width">
          <mat-label>Naziv dobavljača *</mat-label>
          <input matInput formControlName="name" required>
        </mat-form-field>
        <div class="form-row">
          <mat-form-field appearance="outline" class="flex-1">
            <mat-label>PIB</mat-label>
            <input matInput formControlName="taxId">
          </mat-form-field>
          <mat-form-field appearance="outline" class="flex-1">
            <mat-label>Matični broj</mat-label>
            <input matInput formControlName="registrationNumber">
          </mat-form-field>
        </div>
      </div>

      <div class="form-section">
        <h3 class="section-title">Adresa</h3>
        <mat-form-field appearance="outline" class="full-width">
          <mat-label>Adresa</mat-label>
          <input matInput formControlName="address">
        </mat-form-field>
        <div class="form-row">
          <mat-form-field appearance="outline" class="flex-2">
            <mat-label>Grad</mat-label>
            <input matInput formControlName="city">
          </mat-form-field>
          <mat-form-field appearance="outline" class="flex-1">
            <mat-label>Poštanski broj</mat-label>
            <input matInput formControlName="postalCode">
          </mat-form-field>
        </div>
      </div>

      <div class="form-section">
        <h3 class="section-title">Kontakt</h3>
        <div class="form-row">
          <mat-form-field appearance="outline" class="flex-1">
            <mat-label>Telefon</mat-label>
            <input matInput formControlName="phone">
          </mat-form-field>
          <mat-form-field appearance="outline" class="flex-1">
            <mat-label>Email</mat-label>
            <input matInput formControlName="email" type="email">
          </mat-form-field>
        </div>
        <mat-form-field appearance="outline" class="full-width">
          <mat-label>Kontakt osoba</mat-label>
          <input matInput formControlName="contactPerson">
        </mat-form-field>
      </div>

      <!-- Bank Accounts Section -->
      <div class="form-section">
        <div class="section-header">
          <h3 class="section-title" style="margin-bottom: 0; border-bottom: none; padding-bottom: 0;">Bankovni računi</h3>
          <button mat-icon-button type="button" (click)="addBankAccount()" matTooltip="Dodaj račun" class="add-account-btn">
            <mat-icon>add_circle</mat-icon>
          </button>
        </div>

        <div formArrayName="bankAccounts" class="bank-accounts-list">
          <div *ngFor="let account of bankAccountsArray.controls; let i = index"
               [formGroupName]="i"
               class="bank-account-item">
            <div class="account-fields">
              <mat-form-field appearance="outline" class="account-number-field">
                <mat-label>Broj računa</mat-label>
                <input matInput formControlName="accountNumber" placeholder="123-456789-01">
              </mat-form-field>
              <mat-form-field appearance="outline" class="bank-name-field">
                <mat-label>Banka</mat-label>
                <input matInput formControlName="bankName" placeholder="Naziv banke">
              </mat-form-field>
            </div>
            <div class="account-actions">
              <mat-checkbox formControlName="isPrimary" (change)="onPrimaryChange(i)">Primarni</mat-checkbox>
              <button mat-icon-button type="button" (click)="removeBankAccount(i)"
                      matTooltip="Ukloni račun" class="remove-account-btn">
                <mat-icon>delete</mat-icon>
              </button>
            </div>
          </div>
          <div *ngIf="bankAccountsArray.length === 0" class="no-accounts">
            <span>Nema dodatih računa</span>
          </div>
        </div>
      </div>

      <div class="form-section">
        <h3 class="section-title">Napomene</h3>
        <mat-form-field appearance="outline" class="full-width">
          <mat-label>Napomene</mat-label>
          <textarea matInput formControlName="notes" rows="3"></textarea>
        </mat-form-field>
      </div>

      <mat-checkbox formControlName="isActive" class="active-checkbox">Aktivan</mat-checkbox>

      <div class="dialog-actions">
        <button mat-stroked-button mat-dialog-close type="button">Odustani</button>
        <button mat-raised-button color="primary" type="submit" [disabled]="form.invalid">Sačuvaj</button>
      </div>
    </form>
  `,
  styles: [`
    .supplier-form {
      padding: 0 24px 16px 24px;
      display: block;
      width: 550px;
      max-width: 100%;
      max-height: 70vh;
      overflow-y: auto;
    }
    .loading-dialog {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 12px;
      padding: 40px 24px;
      color: #6B7280;
    }
    .form-section {
      margin-bottom: 16px;
    }
    .section-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-bottom: 12px;
      padding-bottom: 8px;
      border-bottom: 1px solid #E5E7EB;
    }
    .section-title {
      font-size: 14px;
      font-weight: 600;
      color: #374151;
      margin: 0 0 12px 0;
      padding-bottom: 8px;
      border-bottom: 1px solid #E5E7EB;
    }
    .full-width {
      width: 100%;
    }
    .form-row {
      display: flex;
      gap: 12px;
    }
    .flex-1 {
      flex: 1;
    }
    .flex-2 {
      flex: 2;
    }
    .active-checkbox {
      margin-top: 8px;
      margin-bottom: 16px;
    }
    .dialog-actions {
      display: flex;
      gap: 8px;
      justify-content: flex-end;
      padding-top: 16px;
      border-top: 1px solid #E5E7EB;
    }
    .add-account-btn {
      color: #3B82F6;
    }
    .bank-accounts-list {
      display: flex;
      flex-direction: column;
      gap: 12px;
    }
    .bank-account-item {
      background: #F9FAFB;
      border: 1px solid #E5E7EB;
      border-radius: 8px;
      padding: 12px;
    }
    .account-fields {
      display: flex;
      gap: 12px;
    }
    .account-number-field {
      flex: 1.5;
    }
    .bank-name-field {
      flex: 1;
    }
    .account-actions {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-top: 4px;
    }
    .remove-account-btn {
      color: #DC2626;
    }
    .no-accounts {
      text-align: center;
      padding: 16px;
      color: #9CA3AF;
      font-size: 13px;
      background: #F9FAFB;
      border-radius: 8px;
      border: 1px dashed #D1D5DB;
    }
  `]
})
export class SupplierDialogComponent implements OnInit {
  private service = inject(SuppliersService);
  loadingBankAccounts = false;

  constructor(
    private fb: FormBuilder,
    private dialogRef: MatDialogRef<SupplierDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: SupplierTotals | null,
  ) {}

  form = this.fb.group({
    name: ['', [Validators.required]],
    address: [''],
    city: [''],
    postalCode: [''],
    taxId: [''],
    registrationNumber: [''],
    phone: [''],
    email: [''],
    contactPerson: [''],
    notes: [''],
    isActive: [true],
    bankAccounts: this.fb.array([])
  });

  get bankAccountsArray(): FormArray {
    return this.form.get('bankAccounts') as FormArray;
  }

  ngOnInit() {
    if (this.data) {
      // Patch basic form data
      this.form.patchValue({
        name: this.data.name,
        address: this.data.address || '',
        city: this.data.city || '',
        postalCode: this.data.postalCode || '',
        taxId: this.data.taxId || '',
        registrationNumber: this.data.registrationNumber || '',
        phone: this.data.phone || '',
        email: this.data.email || '',
        contactPerson: this.data.contactPerson || '',
        notes: this.data.notes || '',
        isActive: this.data.isActive
      });

      // Load supplier details to get bank accounts
      this.loadingBankAccounts = true;
      this.service.getDetail(this.data.id).subscribe({
        next: (detail) => {
          if (detail.bankAccounts) {
            detail.bankAccounts.forEach(ba => this.addBankAccountWithData(ba));
          }
          this.loadingBankAccounts = false;
        },
        error: () => {
          this.loadingBankAccounts = false;
        }
      });
    }
  }

  addBankAccount() {
    const group = this.fb.group({
      id: [null as string | null],
      accountNumber: ['', Validators.required],
      bankName: [''],
      isPrimary: [this.bankAccountsArray.length === 0],
      isActive: [true]
    });
    this.bankAccountsArray.push(group);
  }

  addBankAccountWithData(ba: BankAccount) {
    const group = this.fb.group({
      id: [ba.id],
      accountNumber: [ba.accountNumber, Validators.required],
      bankName: [ba.bankName || ''],
      isPrimary: [ba.isPrimary],
      isActive: [ba.isActive]
    });
    this.bankAccountsArray.push(group);
  }

  removeBankAccount(index: number) {
    this.bankAccountsArray.removeAt(index);
  }

  onPrimaryChange(index: number) {
    // Ensure only one account is primary
    const isPrimary = this.bankAccountsArray.at(index).get('isPrimary')?.value;
    if (isPrimary) {
      this.bankAccountsArray.controls.forEach((ctrl, i) => {
        if (i !== index) {
          ctrl.get('isPrimary')?.setValue(false);
        }
      });
    }
  }

  save() {
    if (this.form.invalid) return;
    const v = this.form.value;

    const bankAccounts: BankAccountRequest[] = (v.bankAccounts || [])
      .filter((ba: any) => ba.accountNumber?.trim())
      .map((ba: any) => ({
        id: ba.id || undefined,
        accountNumber: ba.accountNumber,
        bankName: ba.bankName || undefined,
        isPrimary: !!ba.isPrimary,
        isActive: ba.isActive !== false
      }));

    const dto: CreateSupplierDto = {
      name: v.name!,
      address: v.address || undefined,
      city: v.city || undefined,
      postalCode: v.postalCode || undefined,
      taxId: v.taxId || undefined,
      registrationNumber: v.registrationNumber || undefined,
      phone: v.phone || undefined,
      email: v.email || undefined,
      contactPerson: v.contactPerson || undefined,
      notes: v.notes || undefined,
      isActive: !!v.isActive,
      bankAccounts
    };
    this.dialogRef.close(dto);
  }
}
